/**
 * prefillFromClient — precarga las secciones de un plan con los datos de un
 * cliente vinculado (perfil + última toma de medidas + histórico de progreso).
 *
 * Se usa en dos flujos distintos:
 *  - `PlanBuilderScreen` al crear un plan nuevo vía `?client=N`
 *  - `TemplatesScreen` al "Usar plantilla" con un cliente seleccionado
 *
 * Todas las funciones MUTAN el `PlanDoc` in-place — están pensadas para correr
 * sobre un plan recién creado, antes de persistirlo en el backend.
 */
import {
  genId,
  sumMealMacros,
  type DietAdjustMode,
  type DietCalculator,
  type DietData,
  type MeasurementsData,
  type PlanDoc,
} from "../../types/plan";
import type { ClientFull } from "../../api/clients";
import { MEASUREMENT_FIELDS, type ClientMeasurement } from "../../api/measurements";
import { computeDietTargets, defaultDietCalculator } from "../../lib/dietCalc";

/** Edad en años a partir de un birthdate YYYY-MM-DD. "" si no se puede calcular. */
export function ageFromBirthdate(birthdate: string | null | undefined): string {
  if (!birthdate) return "";
  const d = new Date(birthdate + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 150 ? String(age) : "";
}

/** Mapea el enum `goal` del cliente a una etiqueta legible para la sección. */
const GOAL_LABELS: Record<string, string> = {
  maintain: "Mantener",
  deficit: "Déficit (perder grasa)",
  surplus: "Superávit (ganar masa)",
  recomp: "Recomposición",
};

/** Mapea el enum `level` del cliente a una etiqueta legible. */
const LEVEL_LABELS: Record<string, string> = {
  beginner: "Principiante",
  intermediate: "Intermedio",
  advanced: "Avanzado",
};

/**
 * Reescala las cantidades de los alimentos de cada día para que el total
 * calórico del día se acerque al `targetKcal`. Mantiene la proporción de
 * macros del plan original (escala todo por el mismo factor). Recalcula los
 * totales kcal/p/c/f de cada comida desde los items escalados.
 *
 * Solo toca días con items estructurados — los días con comidas en texto
 * libre (sin `items`) quedan intactos porque no hay nada cuantificable que
 * escalar. Es un punto de partida: el coach ajusta alimentos y cantidades
 * después según la persona.
 */
function rescaleDietDaysToTarget(diet: DietData, targetKcal: number): void {
  for (const day of diet.days || []) {
    // kcal actuales del día sumando los items (escalados por qty/baseQty).
    let dayKcal = 0;
    for (const meal of day.meals || []) {
      if (meal.items && meal.items.length) {
        dayKcal += sumMealMacros(meal.items).kcal;
      }
    }
    if (dayKcal <= 0) continue; // día sin items → no se puede escalar

    const scale = targetKcal / dayKcal;
    // Guard: si el plan origen estaba absurdamente lejos, evitamos escalas
    // disparatadas que generen cantidades sin sentido. Limitamos a [0.4, 2.5].
    const safeScale = Math.min(2.5, Math.max(0.4, scale));

    for (const meal of day.meals || []) {
      if (!meal.items || !meal.items.length) continue;
      for (const it of meal.items) {
        it.qty = Math.max(1, Math.round(it.qty * safeScale));
      }
      // Recalcular totales de la comida desde los items ya escalados.
      const m = sumMealMacros(meal.items);
      meal.kcal = String(Math.round(m.kcal));
      meal.p = String(Math.round(m.p));
      meal.c = String(Math.round(m.c));
      meal.f = String(Math.round(m.f));
    }
  }
}

/** Mapea el `goal` del cliente al modo de ajuste de la calculadora nutricional. */
const GOAL_TO_ADJUST: Record<string, DietAdjustMode> = {
  maintain: "maintain",
  deficit: "deficit",
  surplus: "surplus",
  recomp: "maintain", // recomp ≈ mantenimiento — el coach ajusta si quiere déficit leve
};

/**
 * Precarga (in-place) las secciones `cover` y `client` de un plan recién creado
 * con los datos del cliente vinculado. Para peso/altura usa la última medición
 * registrada (`measurements[0]` — el endpoint devuelve más reciente primero) y,
 * si falta, cae al campo del perfil del cliente.
 */
export function prefillSectionsFromClient(
  plan: PlanDoc,
  client: ClientFull,
  measurements: ClientMeasurement[],
): void {
  const latest = measurements.length > 0 ? measurements[0] : null;

  const weight =
    latest?.weight_kg != null
      ? String(latest.weight_kg)
      : client.current_weight_kg != null
      ? String(client.current_weight_kg)
      : "";
  const rawHeight =
    latest?.height_cm != null
      ? latest.height_cm
      : client.height_cm != null
      ? client.height_cm
      : null;
  // Normaliza altura en metros (ej. 1.8) a cm — datos cargados mal antes del fix.
  const height =
    rawHeight != null ? String(rawHeight > 0 && rawHeight < 3 ? Math.round(rawHeight * 100) : rawHeight) : "";

  for (const s of plan.sections) {
    if (s.type === "cover") {
      (s.data as any).clientName = client.full_name;
    }
    if (s.type === "client") {
      const d = s.data as any;
      d.fullName = client.full_name;
      d.birthdate = client.birthdate || "";
      d.age = ageFromBirthdate(client.birthdate);
      d.weight = weight;
      d.height = height;
      d.goal = client.goal ? GOAL_LABELS[client.goal] || client.goal : "";
      d.level = client.level ? LEVEL_LABELS[client.level] || client.level : "";
      d.injuries = client.injuries || "";
      // startDate/endDate quedan vacíos: el plan define su propio rango, no el
      // del programa global del cliente.
    }
    if (s.type === "diet") {
      // Sembrar la calculadora nutricional con los datos del cliente para que
      // el objetivo calórico + macros se calculen solos.
      const d = s.data as DietData;
      const ageNum = Number(ageFromBirthdate(client.birthdate)) || 0;

      // Si ya había calculadora (caso DUPLICAR: viene del plan origen),
      // conservamos las decisiones METODOLÓGICAS del coach (proteína/grasa
      // por kg, preset e intensidad de ajuste). Solo recalculamos los datos
      // de la PERSONA (peso/altura/edad/sexo) y el objetivo según el goal del
      // cliente destino. Al crear plan en blanco/plantilla no hay calc previa
      // → se usan los defaults.
      const prev: DietCalculator | null =
        d.calculator && typeof d.calculator === "object" ? d.calculator : null;

      d.calculator = defaultDietCalculator({
        weightKg: Number(weight) || 0,
        heightCm: Number(height) || 0,
        age: ageNum,
        sex: client.sex === "F" ? "F" : "M",
        activity: client.activity_level || prev?.activity || "moderate",
        adjustMode: client.goal
          ? GOAL_TO_ADJUST[client.goal] || "maintain"
          : prev?.adjustMode || "maintain",
        // Conservar config metodológica del coach si existía:
        ...(prev
          ? {
              proteinPerKg: prev.proteinPerKg,
              fatPerKg: prev.fatPerKg,
              adjustPreset: prev.adjustPreset,
              adjustPct: prev.adjustPct,
            }
          : {}),
      });

      // Reescalar las cantidades de cada día de comida para acercarse al
      // objetivo calórico del cliente destino. Es un punto de partida — el
      // coach ajusta alimentos/cantidades después. Solo aplica a días con
      // items estructurados (los de texto libre quedan intactos).
      const targets = computeDietTargets(d.calculator);
      if (targets.valid && targets.targetKcal > 0) {
        rescaleDietDaysToTarget(d, targets.targetKcal);
      }
    }
  }
}

/**
 * Precarga (in-place) la sección `measurements` de un plan recién creado con
 * el histórico de medidas del cliente. Para los perímetros que tiene la ficha
 * del cliente (`waist_cm`, `neck_cm`, `hip_cm`, etc.) cae al valor del cliente
 * si la última toma no lo trae — así una ficha con datos básicos pero pocas
 * tomas no queda en blanco.
 *
 * Tomamos hasta 4 mediciones (más reciente primero) y las revertimos a orden
 * cronológico ascendente para que las columnas lean como una evolución. Solo
 * se incluyen las filas con al menos un valor registrado.
 */
/**
 * Siembra SOLO la calculadora nutricional (peso/altura/edad/sexo/actividad/
 * objetivo) desde el cliente, sin tocar los macros ni las comidas ni las demás
 * secciones. Se usa en el flujo de IA: el plan lo arma el modelo, pero la
 * calculadora venía en blanco (defaults 0/M) y el coach tenía que llenarla a
 * mano. A diferencia de `prefillSectionsFromClient`, NO reescala las comidas ni
 * pisa la sección client/cover (todo eso ya lo generó la IA).
 */
export function seedDietCalculatorFromClient(
  plan: PlanDoc,
  client: ClientFull,
  measurements: ClientMeasurement[],
): void {
  const latest = measurements.length > 0 ? measurements[0] : null;
  const weight =
    latest?.weight_kg != null
      ? Number(latest.weight_kg)
      : client.current_weight_kg != null
      ? Number(client.current_weight_kg)
      : 0;
  const rawHeight =
    latest?.height_cm != null
      ? latest.height_cm
      : client.height_cm != null
      ? client.height_cm
      : 0;
  const heightCm = rawHeight > 0 && rawHeight < 3 ? Math.round(rawHeight * 100) : rawHeight || 0;
  const ageNum = Number(ageFromBirthdate(client.birthdate)) || 0;

  for (const s of plan.sections) {
    if (s.type !== "diet") continue;
    const d = s.data as DietData;
    const prev: DietCalculator | null =
      d.calculator && typeof d.calculator === "object" ? d.calculator : null;
    d.calculator = defaultDietCalculator({
      weightKg: weight || 0,
      heightCm,
      age: ageNum,
      sex: client.sex === "F" ? "F" : "M",
      activity: client.activity_level || prev?.activity || "moderate",
      adjustMode: client.goal
        ? GOAL_TO_ADJUST[client.goal] || "maintain"
        : prev?.adjustMode || "maintain",
      ...(prev
        ? {
            proteinPerKg: prev.proteinPerKg,
            fatPerKg: prev.fatPerKg,
            adjustPreset: prev.adjustPreset,
            adjustPct: prev.adjustPct,
          }
        : {}),
    });
  }
}

export function prefillMeasurementsSection(
  plan: PlanDoc,
  client: ClientFull,
  measurements: ClientMeasurement[],
): void {
  const section = plan.sections.find((s) => s.type === "measurements");
  if (!section) return;

  const takes = measurements.slice(0, 4).reverse();
  const columns = takes.length > 0 ? takes.map((m) => m.measured_at || "") : ["Inicial"];

  // Algunos perímetros viven en `al_client` directamente — los usamos como
  // fallback si la medición más reciente no los trae.
  const clientFallback: Partial<Record<keyof ClientMeasurement, number | null>> = {
    weight_kg: client.current_weight_kg as any,
    height_cm: client.height_cm as any,
    neck_cm: client.neck_cm as any,
    waist_cm: client.waist_cm as any,
    hip_cm: client.hip_cm as any,
  };
  const latest = takes[takes.length - 1]; // la última en orden cronológico = la más reciente

  const rows = MEASUREMENT_FIELDS.map((field) => {
    const values = takes.map((m) => {
      const v = m[field.key as keyof ClientMeasurement];
      return v != null ? String(v) : "";
    });
    // Si la toma más reciente quedó vacía pero el cliente tiene el valor en
    // su ficha, lo usamos como fallback (solo en la última columna).
    if (values.length > 0 && values[values.length - 1] === "") {
      const fb = clientFallback[field.key as keyof ClientMeasurement];
      if (fb != null) values[values.length - 1] = String(fb);
    } else if (takes.length === 0) {
      // Sin mediciones todavía: una columna "Inicial" con el fallback del cliente.
      const fb = clientFallback[field.key as keyof ClientMeasurement];
      values.push(fb != null ? String(fb) : "");
    }
    return {
      id: genId("mr"),
      label: field.label,
      unit: field.unit,
      values,
      _hasData: values.some((v) => v !== ""),
    };
  });

  const filled = rows.filter((r) => r._hasData);
  if (filled.length === 0) return;

  void latest;
  const md = section.data as MeasurementsData;
  md.columns = columns;
  md.rows = filled.map(({ _hasData, ...r }) => {
    void _hasData;
    return r;
  });
}
